class ProductStockMessage extends HTMLElement {
  constructor() {
    super();
    this.debug = false; // Set to true for debugging
    this.accessToken = window.__VPV?.shop?.storefrontToken;
    this.store = window.__VPV?.shop?.name;
    this.locale = window.__VPV?.shop?.locale || 'en-US';

    this.headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': this.accessToken,
      'Accept': 'application/json',
      'Accept-Language': this.locale,
    };

    this.productCardThreshold = window.__VPV?.productCardStockThreshold || 50;
    this.variantThreshold = window.__VPV?.variantStockThreshold || 10;

    this.hasFetched = false;
    this.isFetching = false;

  }

  connectedCallback() {
    const id = this.dataset.variantId || this.dataset.productId;

    if (this.getAttribute("data-initialized-for") === id) {
      if (this.debug) console.log("[ProductStockMessage] Already initialized for ID:", id);
      return;
    }

    this.setAttribute("data-initialized-for", id);
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.context = this.dataset.context || "pdp";

    if (this.debug) console.log(`[ProductStockMessage] Connected - ID: ${id}`);
    this.hasFetched = false;

    // ✅ Always fetch immediately in cart contexts
    if (this.context === "mini-cart") {
      this.fetchStockData();

      const updateHandler = () => {
        this.hasFetched = false;
        this.fetchStockData();
      };

      document.addEventListener("cart:updated", updateHandler);
      document.addEventListener("cart:change", updateHandler);
      return;
    }

    // PLP
    if (this.context === "product-card") {
      this.fetchStockData();
      return;
    }

    // PDP / everything else can use observer if you want
    this.initObserver();
  }



  initObserver() {
    if (this.hasFetched) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (this.hasFetched) return;

        this.fetchStockData();
        obs.disconnect();
      });
    }, { rootMargin: '200px' });

    observer.observe(this);
  }



  static get observedAttributes() {
    return ['data-product-id', 'data-variant-id'];
  }

  // attributeChangedCallback(name, oldValue, newValue) {
  //   if (name === 'data-variant-id' && oldValue !== newValue) {
  //     this.variantId = newValue;
  //     this.hasFetched = false;
  //     this.fetchStockData();
  //   }
  // }

  // Add this method explicitly
  updateVariantId(newVariantId) {
    if (this.variantId === newVariantId && this.hasFetched) {
      if (this.debug) console.log('Variant ID unchanged and already fetched — skipping');
      return;
    }

    if (this.debug) console.log(`[ProductStockMessage] Updating variant ID: ${newVariantId}`);
    this.setAttribute('data-variant-id', newVariantId);
    this.variantId = newVariantId;
    this.hasFetched = false;
    this.fetchStockData();
  }

  updateProductId(newProductId) {
    if (this.productId === newProductId) {
      if (this.debug) console.log('Product ID has not changed, skipping fetch');
      return;
    }
    this.setAttribute('data-product-id', newProductId);
    this.productId = newProductId;
    this.hasFetched = false;
    this.fetchStockData();
  }

  async fetchStockData() {
    if (this.hasFetched || this.isFetching) return;

    const id = this.productId || this.variantId;
    if (!id) return;

    const type = this.productId ? 'product' : 'variant';
    await this.fetchInventoryData({ type, id });
  }



  async fetchInventoryData({ type, id }) {
    if (!id) return;

    // Special handling for PLP (product-card)
    if (this.context === "product-card" && this.productId) {
      const result = await this.sendQuery(
        `
        query GetProductVariantsStock($productId: ID!) {
          product(id: $productId) {
            variants(first: 100) {
              edges {
                node {
                  quantityAvailable
                }
              }
            }
          }
        }
      `,
        {
          productId: `gid://shopify/Product/${this.productId}`,
        }
      );

      const variants = result?.data?.product?.variants?.edges || [];
      const totalQty = variants.reduce(
        (sum, edge) => sum + (edge.node.quantityAvailable || 0),
        0
      );

      if (this.debug) {
        console.log(
          `[ProductStockMessage] PLP total variant quantity: ${totalQty}`
        );
      }

      this.updateMessage(totalQty);
      this.hasFetched = true;
      return;
    }

    // Default handling for PDP and Quick View
    const queries = {
      variant: {
        query: `
          query GetVariantStock($variantId: ID!) {
            node(id: $variantId) {
              ... on ProductVariant {
                id
                quantityAvailable
              }
            }
          }
        `,
        variables: { variantId: `gid://shopify/ProductVariant/${id}` },
        extract: (data) => data?.node?.quantityAvailable || 0,
      },
      product: {
        query: `
          query GetProductTotalInventory($productId: ID!) {
            product(id: $productId) {
              title
              totalInventory
            }
          }
        `,
        variables: { productId: `gid://shopify/Product/${id}` },
        extract: (data) => data?.product?.totalInventory || 0,
      },
    };

    const config = queries[type];
    if (!config) throw new Error(`Unsupported type: ${type}`);

    const result = await this.sendQuery(config.query, config.variables);
    const quantityAvailable = config.extract(result?.data);

    if (this.debug) {
      console.log(
        `[ProductStockMessage] Fetched stock data for ${type} ID: ${id}, quantity: ${quantityAvailable}`
      );
    }

    this.updateMessage(quantityAvailable);
    this.hasFetched = true;
  }

  updateMessage(quantityAvailable) {
      if (this.debug) console.log(`[ProductStockMessage] Updating message ${this.variantId} with quantity: ${quantityAvailable}`);
      let message = '';
      let visibilityClass = 'hidden';
      if (quantityAvailable > 0 && quantityAvailable < this.variantThreshold) {
        message = `Only ${quantityAvailable} left`;
        visibilityClass = 'is-visible';
      } else if (quantityAvailable >= this.variantThreshold && quantityAvailable < this.productCardThreshold) {
        message = 'Low Stock';
        visibilityClass = 'is-visible';
      }

      if (this.context === 'mini-cart') {
        if (this.debug) console.log('[ProductStockMessage][mini-cart] quantityAvailable:', quantityAvailable, 'message:', message, 'visibilityClass:', visibilityClass);
      }

      this.renderMessage(message, visibilityClass);
  }

  renderMessage(message, visibilityClass) {
    if (this.lastMessage === message && this.lastVisibility === visibilityClass) return;
    this.lastMessage = message;
    this.lastVisibility = visibilityClass;

    if (this.debug) console.log(`[ProductStockMessage] Rendering message: "${message}" with visibility: ${visibilityClass}`);
    if (this.context === 'pdp') {
      const blockItems = document.querySelectorAll('.product-info__block-item[data-block-type="low-stock"]');
      blockItems.forEach(item => item.classList.toggle('is-visible', visibilityClass === 'is-visible'));

      this.textContent = message;

      const form = document.querySelector(".product-info__buy-buttons form");
      if (form) {
        // Remove any existing low_stock properties
        const existingInputs = form.querySelectorAll(
          'input[name="properties[low_stock]"]'
        );
        existingInputs.forEach((input) => input.remove());

        // Also remove any properties that contain "low_stock" in the name
        const allPropertyInputs = form.querySelectorAll(
          'input[name*="properties"]'
        );
        allPropertyInputs.forEach((input) => {
          if (
            input.name.toLowerCase().includes("low_stock") ||
            input.name.toLowerCase().includes("low-stock")
          ) {
            input.remove();
          }
        });
      }

    } else if (this.context === 'mini-cart') {
      this.innerHTML = visibilityClass === 'is-visible' ? `
        <div class="product-stock-message low-stock">
          <span class="icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true" focusable="false">
              <circle cx="4" cy="4" r="4" fill="#F7CD5F"/>
            </svg>
          </span>
          <span class="badge--mini-cart--message">${message}</span>
        </div>` : '';
      this.classList.toggle('hidden', visibilityClass === 'hidden');
    } else if (this.context === 'product-card') {
      this.classList.toggle('is-visible', visibilityClass === 'is-visible');
      this.innerHTML = visibilityClass === 'is-visible' ? 'Low stock' : '';
    }
  }

  async sendQuery(query, variables = {}) {
    if (!this.store || !this.accessToken) {
      if (this.debug) {
        console.warn("[ProductStockMessage] Missing store or storefront token", {
          store: this.store,
          hasToken: !!this.accessToken,
          context: this.context,
          variantId: this.variantId,
          productId: this.productId,
        });
      }
      return null;
    }

    const body = JSON.stringify({ query, variables });

    const response = await fetch(`https://${this.store}.myshopify.com/api/2025-01/graphql.json`, {
      headers: this.headers,
      method: 'POST',
      body,
    });

    let json;
    try {
      json = await response.json();
    } catch (e) {
      if (this.debug) console.error("[ProductStockMessage] GraphQL non-JSON response", e);
      return null;
    }

    if (!response.ok || json?.errors) {
      if (this.debug) {
        console.error("[ProductStockMessage] GraphQL error", {
          status: response.status,
          ok: response.ok,
          errors: json?.errors,
          variables,
          context: this.context,
        });
      }
    }

    return json;
  }

}

if (!customElements.get('product-stock-message')) {
  customElements.define('product-stock-message', ProductStockMessage);
}
